import { useDebouncedState } from "@mantine/hooks";
import { Tab } from "@mui/base/Tab";
import { TabPanel } from "@mui/base/TabPanel";
import { Tabs } from "@mui/base/Tabs";
import { TabsList } from "@mui/base/TabsList";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import Switch from "~/components/switch/Switch";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { useEndpointAccess } from "~/hooks/useEndpointAccess";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { BillingOverview } from "../../../components/billing/BillingOverview";
import NoData from "../../../components/containers/NoData";
import { Invoices } from "../../../components/invoices/Invoices";
import { PaginationConfig } from "../../../components/ui/pagination/Pagination";
import labels from "../../../helpers/labels";
import styles from "./InvoicesPage.module.scss";

const InvoicePage = () => {
  usePageTitle(PAGE_TITLES.invoices);
  const { team, refetchTeam } = useTeam();
  const queryClient = useQueryClient();
  // The backend closes the invoices to some teams (e.g. during an Autopto
  // trial): send them back to the billing page instead of "No invoices".
  const canOpenInvoices = useEndpointAccess("invoices");

  const { mutate: updateEmailInvoice, isPending: isUpdatingEmailInvoice } =
    useMutation({
      mutationFn: async (email_invoice: boolean) => {
        const response = await apiClient.teams.teamsPartialUpdate(
          team?.id ?? 0,
          { email_invoice },
        );

        return response.data;
      },
      onSuccess: async () => {
        enqueueSnackbar("Invoice email preference updated", {
          variant: "success",
        });
        await refetchTeam();
        await queryClient.refetchQueries({ queryKey: ["teams"] });
      },
      onError: () => {
        enqueueSnackbar("Failed to update invoice email preference", {
          variant: "error",
          persist: true,
        });
      },
    });
  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState("", 500);

  useEffect(() => {
    setDebouncedSearch(searchValue.trim());
  }, [searchValue, setDebouncedSearch]);

  // Reset page when filters change.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch, sorting, pagination.itemsPerPage]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "invoices",
      pagination.itemsPerPage,
      pagination.currentPage,
      sorting,
      debouncedSearch,
      team?.id,
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const sortString =
        sorting.length > 0
          ? sorting.map((s) => `${s.id} ${s.desc ? "DESC" : "ASC"}`).join(", ")
          : undefined;

      const response = await apiClient.billing.invoicesList({
        "page[size]": pagination.itemsPerPage,
        "page[number]": pagination.currentPage,
        s: sortString,
        ...(debouncedSearch && {
          q: {
            // @ts-expect-error ransack _cont not in generated types
            number_cont: debouncedSearch,
          },
        }),
      });
      const { headers } = response;
      setPagination((prev: PaginationConfig) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count")),
        nextPage: Number(headers.get("x-pager-next-page")),
        prevPage: Number(headers.get("x-pager-prev-page")),
        totalPages: Number(headers.get("x-pager-total-pages")),
      }));

      return response;
    },
  });
  const { data: upcomingInvoices, isLoading: upcomingInvoicesLoading } =
    useQuery({
      queryKey: ["upcomingInvoices", team?.id],
      queryFn: async () => {
        return await apiClient.billing.invoicesUpcomingList();
      },
    });
  const {
    mutate: getInvoiceStripeUrl,
    isPending: isLoadingGetInvoiceStripeUrl,
  } = useMutation({
    mutationFn: async (id: number) => {
      const data = await apiClient.billing.invoicesViewDetail(id);
      // @ts-expect-error BE types
      const stipeUrl: string | undefined = data?.data?.invoice_url?.url;
      if (stipeUrl) {
        window.open(stipeUrl, "_blank");
      }

      return data;
    },
    onSuccess: (data) => {
      // @ts-expect-error BE types
      if (data?.data?.invoice_url?.url === null) {
        enqueueSnackbar(`The invoice is no longer available!`, {
          variant: "error",
          persist: true,
        });
      }
    },
  });
  const { mutate: downloadInvoice, isPending: isLoadingDownloadInvoice } =
    useMutation({
      mutationFn: async (id: number) => {
        const data = await apiClient.billing.invoicesDownloadDetail(id);
        // @ts-expect-error BE types
        const downloadUrl: string | undefined = data?.data?.invoice_url?.url;
        if (downloadUrl) {
          const link = document.createElement("a");
          link.href = downloadUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        return data;
      },
      onSuccess: (data) => {
        // @ts-expect-error BE types
        if (data?.data?.invoice_url?.url === null) {
          enqueueSnackbar(`The invoice is no longer available!`, {
            variant: "error",
            persist: true,
          });
        }
      },
    });

  const isHistoryInvoicesNotEmpty =
    data?.data?.["payments/invoices"] &&
    data?.data?.["payments/invoices"].length > 0;
  const isUpcomingInvoicesNotEmpty =
    // @ts-expect-error BE types
    upcomingInvoices?.data?.invoice_upcoming &&
    // @ts-expect-error BE types
    Object.keys(upcomingInvoices.data.invoice_upcoming).length > 0;

  if (canOpenInvoices === false) {
    return <Navigate to={routes.billing.subscription()} replace />;
  }

  if (upcomingInvoicesLoading) return <Spinner />;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{PAGE_TITLES.invoices}</h1>
      <Tabs defaultValue={labels.UPCOMING_INVOICES}>
        <TabsList className={styles.tabsList}>
          <Tab className={styles.tabButton} value={labels.UPCOMING_INVOICES}>
            {labels.UPCOMING_INVOICES}
          </Tab>
          <Tab className={styles.tabButton} value={labels.HISTORY_INVOICES}>
            {labels.HISTORY_INVOICES}
          </Tab>
        </TabsList>
        <label className={styles.emailToggle}>
          <Switch
            checked={team?.email_invoice ?? false}
            onChange={(e) => updateEmailInvoice(e.target.checked)}
            disabled={isUpdatingEmailInvoice}
          />
          <span>Email my invoice each month</span>
        </label>
        <TabPanel value={labels.UPCOMING_INVOICES}>
          {isUpcomingInvoicesNotEmpty ? (
            <BillingOverview
              // @ts-expect-error BE types
              billingData={upcomingInvoices?.data?.invoice_upcoming}
            />
          ) : (
            <NoData title="No Upcoming Invoices" />
          )}
        </TabPanel>
        <TabPanel value={labels.HISTORY_INVOICES}>
          {!isHistoryInvoicesNotEmpty && !isLoading ? (
            <NoData title="No Invoices History" />
          ) : (
            <Invoices
              isLoading={isLoading}
              isFetching={isFetching}
              sorting={sorting}
              onSortingChange={setSorting}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              pagination={pagination}
              setPagination={setPagination}
              isLoadingDownloadInvoice={isLoadingDownloadInvoice}
              invoices={data?.data?.["payments/invoices"] ?? []}
              getInvoiceStripeUrl={getInvoiceStripeUrl}
              downloadInvoice={downloadInvoice}
              isLoadingGetInvoiceStripeUrl={isLoadingGetInvoiceStripeUrl}
            />
          )}
        </TabPanel>
      </Tabs>
    </div>
  );
};
export default InvoicePage;
